import React, { useEffect, useMemo, useState } from "react";
import {
  X, MapPin, TrendingUp, Shield, Flame, Newspaper, Wheat, Bot,
  ChevronLeft, ChevronRight, Activity, Megaphone,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { titleCase, formatDateID } from "../lib/format";

const SENT_COLOR = { positive: "#10b981", neutral: "#f59e0b", negative: "#ef4444" };

const PANGAN_LABEL = { aman: "RENDAH", siaga: "SEDANG", waspada: "TINGGI", kritis: "KRITIS" };
const panganLabel = (lvl) => PANGAN_LABEL[(lvl || "").toLowerCase()] || (lvl || "—").toUpperCase();

const levelColor = (lvl) => {
  const t = (lvl || "").toLowerCase();
  if (t === "kritis" || t === "tinggi" || t === "kritis_") return "#ef4444";
  if (t === "waspada" || t === "sedang") return "#f59e0b";
  if (t === "siaga") return "#facc15";
  return "#10b981";
};

const intensityColor = (pct) => {
  if (pct >= 60) return "#ef4444";
  if (pct >= 30) return "#f59e0b";
  return "#10b981";
};

const sigBadge = (scale) => {
  const t = (scale || "").toLowerCase();
  if (t.includes("kritis") || t.includes("tinggi")) return { bg: "rgba(239,68,68,0.15)", color: "#ef4444", label: t.toUpperCase() || "TINGGI" };
  if (t.includes("sedang")) return { bg: "rgba(245,158,11,0.15)", color: "#f59e0b", label: t.toUpperCase() };
  return { bg: "rgba(16,185,129,0.15)", color: "#10b981", label: t.toUpperCase() || "RENDAH" };
};

export default function RegionSummaryModal({ open, region, onClose }) {
  const [risiko, setRisiko] = useState(null);
  const [kamtib, setKamtib] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [sentNews, setSentNews] = useState(null);
  const [ai, setAi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [insightIdx, setInsightIdx] = useState(0);

  useEffect(() => {
    if (!open || !region?.code) return;
    setLoading(true);
    setInsightIdx(0);
    const code = region.code;
    const controller = new AbortController();
    const signal = controller.signal;
    Promise.all([
      apiClient.get(`/proxy/get_risiko_detail?region=${code}`, { signal }).then((r) => r.data).catch(() => null),
      apiClient.get(`/kamtibmas?year=2026`, { signal }).then((r) => r.data).catch(() => null),
      apiClient.get(`/commodity/v3?region=${code}`, { signal }).then((r) => r.data).catch(() => null),
      apiClient.get(`/proxy/get_sentiment_news?sentiment=all&limit=500`, { signal }).then((r) => r.data).catch(() => null),
      apiClient.get(`/ai-summary`, { signal }).then((r) => r.data).catch(() => null),
    ])
      .then(([rr, kk, cc, sn, aa]) => {
        if (signal.aborted) return;
        setRisiko(rr?.details || null);
        const provs = kk?.provinces || [];
        setKamtib(provs.find((p) => String(p.code) === String(code)) || null);
        // upstream commodity v3 returns top-level deviations array
        const devs = cc?.deviations || cc?.data?.deviations || [];
        setCommodities(devs);
        setSentNews(sn?.data || null);
        const aiList = Array.isArray(aa?.data) ? aa.data : [];
        setAi(aiList.filter((x) => String(x.region_code) === String(code)));
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, region?.code]);

  // ---------- ISU POPULER (region issues) ----------
  const regionIssues = useMemo(() => {
    if (!sentNews || !region?.name) return [];
    const target = region.name.toUpperCase();
    return (sentNews.issues || [])
      .filter((i) => (i.province || "").toUpperCase() === target)
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 3);
  }, [sentNews, region]);

  // ---------- SENTIMEN MEDIA (region news) ----------
  const regionNews = useMemo(() => {
    if (!sentNews || !regionIssues.length) return [];
    const ids = new Set();
    regionIssues.forEach((i) => (i.news_idx || []).forEach((nid) => ids.add(nid)));
    return (sentNews.news || []).filter((n) => ids.has(n.news_id)).slice(0, 3);
  }, [sentNews, regionIssues]);

  const sentCounts = useMemo(() => {
    const c = { pos: 0, neg: 0, neu: 0 };
    regionNews.forEach((n) => {
      if (n.sentiment === "positive") c.pos++;
      else if (n.sentiment === "negative") c.neg++;
      else c.neu++;
    });
    return c;
  }, [regionNews]);

  const totalSent = sentCounts.pos + sentCounts.neg + sentCounts.neu || 1;
  const posPct = Math.round((sentCounts.pos / totalSent) * 100);
  const negPct = Math.round((sentCounts.neg / totalSent) * 100);

  // ---------- KOMODITAS DEVIASI ----------
  const komList = useMemo(() => {
    return (commodities || [])
      .map((c) => {
        const name = c.name;
        const price = Number(c.price ?? 0);
        const baseline = c.baseline != null ? Number(c.baseline) : null;
        const deviation = Number(c.deviation ?? 0);
        const base = baseline != null ? baseline : (deviation !== 0 ? price / (1 + deviation / 100) : price);
        return { name, price, base, deviation, status: c.status };
      })
      .filter((c) => c.name)
      .sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation))
      .slice(0, 6);
  }, [commodities]);

  const currentInsight = ai[insightIdx] || null;

  if (!open || !region) return null;

  return (
    <div className="nms-modal-backdrop full-modal" data-testid="region-summary-modal" onClick={onClose}>
      <div className="rs-modal" onClick={(e) => e.stopPropagation()}>
        <header className="rs-head">
          <div className="rs-head-left">
            <div className="rs-head-icon"><MapPin size={18} /></div>
            <h2 className="rs-title">RINGKASAN <span>WILAYAH</span></h2>
            <span className="rs-region-badge"><MapPin size={11} /> {titleCase(region.name)}</span>
          </div>
          <button className="rs-close" onClick={onClose} aria-label="Close" data-testid="rs-close" type="button">
            <X size={18} />
          </button>
        </header>

        {loading && <div className="rs-loading" data-testid="rs-loading">Memuat data wilayah…</div>}

        {!loading && (
          <div className="rs-grid">
            {/* INFLASI */}
            <section className="rs-card rs-inflasi" data-testid="rs-inflasi">
              <div className="rs-card-head">
                <TrendingUp size={14} /> <h3>INFLASI</h3>
              </div>
              <div className="rs-inflasi-grid">
                <div className="rs-inflasi-box">
                  <span className="rs-mini-lbl">MoM</span>
                  <span className="rs-mini-val">{(risiko?.["Inflasi MoM (%)"] ?? 0).toFixed(2)}%</span>
                  <span className="rs-mini-status" style={{ color: levelColor(risiko?.["level_Inflasi MoM (%)"]) }}>
                    {(risiko?.["level_Inflasi MoM (%)"] || "—").toUpperCase()}
                  </span>
                </div>
                <div className="rs-inflasi-box">
                  <span className="rs-mini-lbl">YoY</span>
                  <span className="rs-mini-val">{(risiko?.["Inflasi YoY (%)"] ?? 0).toFixed(2)}%</span>
                  <span className="rs-mini-status" style={{ color: levelColor(risiko?.["level_Inflasi YoY (%)"]) }}>
                    {(risiko?.["level_Inflasi YoY (%)"] || "—").toUpperCase()}
                  </span>
                </div>
              </div>
            </section>

            {/* KAMTIBMAS */}
            <section className="rs-card rs-kamtibmas" data-testid="rs-kamtibmas">
              <div className="rs-card-head">
                <Shield size={14} /> <h3>KAMTIBMAS</h3>
              </div>
              <div className="rs-kam-main">
                <div className="rs-kam-num">
                  <span className="rs-big-num">{(kamtib?.total || 0).toLocaleString("id-ID")}</span>
                  <span className="rs-kam-lbl">INSIDEN</span>
                </div>
                <div className="rs-kam-right">
                  <span className="rs-kam-status" style={{
                    color: levelColor(risiko?.level_skor_kamtibmas || risiko?.["level_skor-kamtibmas"]),
                    background: `${levelColor(risiko?.level_skor_kamtibmas || risiko?.["level_skor-kamtibmas"])}1f`,
                  }}>
                    ● {(risiko?.["level_skor-kamtibmas"] || risiko?.level_skor_kamtibmas || "—").toUpperCase()}
                  </span>
                  <div className="rs-kam-idx">
                    <span className="rs-kam-idx-lbl">INDEX RISK</span>
                    <span className="rs-kam-idx-val" style={{ color: levelColor(risiko?.["level_skor-kamtibmas"]) }}>
                      {(risiko?.["skor-kamtibmas"] ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="rs-kam-grid">
                {[
                  ["KEJAHATAN", kamtib?.categories?.kejahatan],
                  ["GANGGUAN", kamtib?.categories?.gangguan],
                  ["PELANGGARAN", kamtib?.categories?.pelanggaran],
                  ["BENCANA", kamtib?.categories?.bencana],
                ].map(([lbl, v]) => (
                  <div key={lbl} className="rs-kam-box">
                    <span className="rs-mini-lbl">{lbl}</span>
                    <span className="rs-mini-val">{(v || 0).toLocaleString("id-ID")}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* ISU POPULER */}
            <section className="rs-card rs-isu" data-testid="rs-isu">
              <div className="rs-card-head">
                <Flame size={14} /> <h3>ISU POPULER</h3>
              </div>
              <div className="rs-isu-list">
                {regionIssues.map((it, idx) => {
                  const pct = it.percentage || 0;
                  const c = intensityColor(pct);
                  const cnt = it.news_count || (it.news_idx || []).length || 0;
                  return (
                    <div key={idx} className="rs-isu-row" data-testid={`rs-isu-${idx}`}>
                      <span className="rs-isu-rank">#{String(cnt).padStart(2, "0")}</span>
                      <div className="rs-isu-body">
                        <div className="rs-isu-meta">
                          <span className="rs-isu-tag" style={{ color: c, background: `${c}1c` }}>
                            {pct >= 60 ? "Tinggi" : pct >= 30 ? "Sedang" : "Rendah"}
                          </span>
                          <span className="rs-isu-int">{pct.toFixed(0)}% INTENSITY</span>
                        </div>
                        <div className="rs-isu-title">{titleCase(it.title)}</div>
                      </div>
                    </div>
                  );
                })}
                {!regionIssues.length && <div className="rs-empty">Tidak ada isu menonjol hari ini</div>}
              </div>
            </section>

            {/* SENTIMEN MEDIA */}
            <section className="rs-card rs-sentimen" data-testid="rs-sentimen">
              <div className="rs-card-head">
                <Newspaper size={14} /> <h3>SENTIMEN MEDIA</h3>
              </div>
              <div className="rs-sent-bar">
                <div className="seg pos" style={{ width: `${posPct}%` }} />
                <div className="seg neu" style={{ width: `${100 - posPct - negPct}%` }} />
                <div className="seg neg" style={{ width: `${negPct}%` }} />
              </div>
              <div className="rs-sent-pct">
                <span style={{ color: "#10b981" }}>{posPct}% POSITIF</span>
                <span style={{ color: "#ef4444" }}>{negPct}% NEGATIF</span>
              </div>
              <div className="rs-sent-news">
                {regionNews.map((n) => (
                  <div key={n.news_id} className="rs-sent-card" data-testid={`rs-news-${n.news_id}`}>
                    <div className="rs-sent-top">
                      <span className="rs-sent-src">{n.source}</span>
                      <span className="rs-sent-tag" style={{
                        color: SENT_COLOR[n.sentiment] || "#94a3b8",
                        background: `${SENT_COLOR[n.sentiment] || "#94a3b8"}22`,
                      }}>{(n.sentiment || "neutral").toUpperCase()}</span>
                    </div>
                    <div className="rs-sent-title" title={n.title}>{n.title}</div>
                  </div>
                ))}
                {!regionNews.length && <div className="rs-empty">Berita regional tidak ditemukan</div>}
              </div>
            </section>

            {/* RISIKO PANGAN */}
            <section className="rs-card rs-pangan" data-testid="rs-pangan">
              <div className="rs-card-head">
                <Wheat size={14} /> <h3>RISIKO PANGAN</h3>
              </div>
              <div className="rs-pangan-top">
                <div>
                  <span className="rs-big-num">{(risiko?.["skor-pangan-agregasi"] ?? 0).toFixed(1)}</span>
                  <span className="rs-kam-lbl">INDEX RISK</span>
                </div>
                <span className="rs-kam-status" style={{
                  color: levelColor(risiko?.["level_skor-pangan-agregasi"]),
                  background: `${levelColor(risiko?.["level_skor-pangan-agregasi"])}1f`,
                }}>
                  ● {panganLabel(risiko?.["level_skor-pangan-agregasi"])}
                </span>
              </div>
              <div className="rs-kom-head">
                <span>KOMODITAS</span>
                <span>DEVIASI</span>
              </div>
              <div className="rs-kom-list">
                {komList.map((c, i) => {
                  const up = c.deviation > 0;
                  return (
                    <div key={i} className="rs-kom-row" data-testid={`rs-kom-${i}`}>
                      <div className="rs-kom-info">
                        <span className="rs-kom-name">{c.name}</span>
                        <span className="rs-kom-base">Base: Rp {c.base.toLocaleString("id-ID")}</span>
                      </div>
                      <div className="rs-kom-right">
                        <span className="rs-kom-price">Rp {c.price.toLocaleString("id-ID")}</span>
                        <span className="rs-kom-dev" style={{ color: up ? "#ef4444" : "#10b981" }}>
                          {up ? "+" : ""}{c.deviation.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
                {!komList.length && <div className="rs-empty">Data komoditas tidak tersedia</div>}
              </div>
            </section>

            {/* EXECUTIVE INSIGHT */}
            <section className="rs-card rs-executive" data-testid="rs-executive">
              <div className="rs-card-head">
                <Bot size={14} /> <h3>EXECUTIVE INSIGHT</h3>
                <div className="rs-exec-pager">
                  <button className="rs-pager-btn" onClick={() => setInsightIdx((i) => Math.max(0, i - 1))} disabled={insightIdx === 0} aria-label="Prev" type="button" data-testid="rs-prev">
                    <ChevronLeft size={14} />
                  </button>
                  <span className="rs-pager-num">
                    <strong>{ai.length ? insightIdx + 1 : 0}</strong> / {ai.length}
                  </span>
                  <button className="rs-pager-btn" onClick={() => setInsightIdx((i) => Math.min(ai.length - 1, i + 1))} disabled={insightIdx >= ai.length - 1} aria-label="Next" type="button" data-testid="rs-next">
                    <ChevronRight size={14} />
                  </button>
                </div>
                <span className="rs-live"><Activity size={9} /> LIVE</span>
              </div>
              {currentInsight ? (
                <>
                  <div className="rs-exec-top">
                    <span className="rs-exec-tag" style={{
                      background: sigBadge(currentInsight.impact?.significance_scale_1_5).bg,
                      color: sigBadge(currentInsight.impact?.significance_scale_1_5).color,
                    }}>{sigBadge(currentInsight.impact?.significance_scale_1_5).label}</span>
                    <span className="rs-exec-date">{formatDateID(currentInsight.date)}</span>
                  </div>
                  <h4 className="rs-exec-title">
                    <span className="rs-exec-hash">#{insightIdx + 1}</span> {titleCase(currentInsight.issue_title)}
                  </h4>
                  <p className="rs-exec-summary">{currentInsight.issue_summary}</p>
                  <div className="rs-exec-cols">
                    <div className="rs-exec-col">
                      <div className="rs-exec-col-head"><Megaphone size={11} /> ANALISIS DAMPAK</div>
                      <p className="rs-exec-col-body">{currentInsight.impact?.description}</p>
                    </div>
                    <div className="rs-exec-col">
                      <div className="rs-exec-col-head"><Bot size={11} /> REKOMENDASI</div>
                      <ul className="rs-exec-list">
                        {(currentInsight.recommendation || []).slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <div className="rs-empty">Tidak ada executive insight untuk wilayah ini</div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
